/**
 * The sidebar, as data.
 *
 * This used to be five array literals inside Layout.jsx, each carrying its own
 * `roles` list, with clinic gating spread inline between them. That made it
 * impossible to answer "what does a data_entry user actually see?" without
 * tracing every literal by hand — and two role mistakes had been sitting in
 * there unnoticed as a result (report_viewer could reach Operations while
 * viewer could not; data_entry could reach Physicians but none of the other
 * config screens).
 *
 * So visibility lives on the individual item, and a group is shown when any of
 * its items survive. Three consequences worth knowing:
 *
 *  - regrouping is purely cosmetic. Moving Zendesk from Support into Operations
 *    does not change who can see Zendesk, because its audience travels with it.
 *  - a group can never render empty, and never renders a header over nothing.
 *  - every role's whole visible tree can be asserted in a test. See
 *    src/config/navigation.test.js — that test is what stops the next reorg
 *    from quietly revoking somebody's access.
 *
 * Group order here is the order on screen. Admin is pinned to the bottom by the
 * renderer, not by its position in this list.
 */

// Icons are per-item and must stay distinct — navigation.test.js asserts it.
// The emoji set this replaced had eleven collisions, which made them useless
// for scanning. Group icons are also distinct from every item icon, so a
// collapsed group never looks like one of its own children.
import {
  LayoutDashboard, XCircle, Bot,
  Settings2, Stethoscope, Pill, ClipboardList, FolderTree, Rocket,
  Wrench, ListChecks, Timer, Map, Building2, Gift, Headphones, Calendar, HeartPulse, Ban,
  CreditCard, Package, Tag, Banknote, Undo2, Repeat, RefreshCw, Search, Activity, Radio,
  BarChart3, Megaphone, Globe, Route, Share2, TrendingUp, Link2, UserRound, MousePointerClick,
  DollarSign, PieChart, Receipt, Layers, Boxes, Clock, TrendingDown, Shuffle, Network,
  GitBranch, ShieldCheck, Gauge, FileText, FileSignature,
  Shield, Users, KeyRound, Plug, Zap, Crosshair, Signal, FolderOpen, GitCompare, Truck, Server,
  Fingerprint,
  ScanFace,
  Scale, CalendarClock, Mail, CalendarDays, MessageSquare, UserPlus, Table2, Target, PersonStanding,
  PhoneCall, Wallet, Contact, Inbox, Landmark,
} from 'lucide-react'

import reportPages from '../../../config/reportPages.json'

import { MANIFOLD_CATALOG_CLINICS, MANIFOLD_TENANT_CLINICS } from '../utils/manifoldClinics'
import { TRAFFIC_CLINICS, LP_CLICK_RATE_CLINICS } from '../utils/trafficClinics'

/** Clinics on the legacy Chargebee-backed payments stack. */
const CHARGEBEE_CLINICS = ['mdexam', 'mdexam_legacy']

/** Clinics on Hyperswitch. */
const HYPERSWITCH_CLINICS = ['mdexam', 'mdexam_legacy', 'mdrx']

/** Clinics with a Zendesk inbox wired up. */
/**
 * Clinics with a Zendesk brand. Keep in step with ZENDESK_BRAND_BY_CLINIC in
 * backend/src/routes/zendesk.js — that map is the authority, this mirrors it,
 * and a clinic listed here but absent there gets a page that says its brand is
 * not supported.
 *
 * All four mdexam ids are here and all resolve to the single "MD Exam" Zendesk
 * brand, so each shows the same tickets. mdexam_legacy used to be listed
 * WITHOUT being in that map, which is why it showed the link and landed on
 * "brand not supported"; it is mapped now.
 */
const ZENDESK_CLINICS = [
  'tmates', 'mdexam', 'mdexam_legacy', 'mdexam-wl', 'mdexam-rrv', 'rose',
  'mdrx', 'scienceco', 'vigore', 'trybalance', 'revora',
  // Support data comes from ad_platform, not the clinic database, so this one
  // works even though its clinical data sits on the pharmacy cluster.
  'bloom_greenstone',
]

const ADMINS = ['super_admin', 'clinic_admin']

/**
 * Day-to-day operational screens.
 *
 * report_viewer is deliberately absent: the role is described to the people
 * assigning it as "Reports and analytics only (no config access)", and it used
 * to reach these while `viewer` — "read-only access to all modules" — could
 * not. The two were the wrong way round.
 */
const OPERATIONS = [...ADMINS, 'viewer']

// Items marked `shareable: true` can still be handed to one report_viewer by
// ticking the page in their page permissions. Only that explicit selection opens
// them; a report_viewer with no page list keeps the role's default reach.

/** Patient-facing support tooling. Read-only enough for report_viewer. */
const SUPPORT = [...ADMINS, 'viewer', 'report_viewer']

/** Anything that answers a money or growth question. */
const ANALYTICS = [...ADMINS, 'viewer', 'report_viewer']

/**
 * Config screens.
 *
 * data_entry is on all of these except Releases. Its description is "create and
 * update records only", but it could previously only reach Physicians, which
 * made the role useless for the other three. Releases stays out: publishing
 * config to production is not a record edit, and it has its own permission flag.
 */
const CONFIG_VIEWERS = [...ADMINS, 'viewer']

export const DASHBOARD = {
  name: 'Dashboard',
  href: '/',
  icon: LayoutDashboard,
  roles: '*',
}

export const NAV_GROUPS = [
  {
    id: 'clinical',
    name: 'Clinical Setup',
    icon: Settings2,
    items: [
      {
        name: 'Physicians', href: '/physicians', icon: Stethoscope,
        roles: [...CONFIG_VIEWERS, 'physician_manager', 'pharmacy_manager', 'program_manager', 'data_entry'],
        permission: 'canManagePhysicians',
      },
      // Core's shared provider directory: not clinic-scoped, so not
      // clinic-gated. Super admins, or anyone with the canManageSharedPhysicians grant.
      { name: 'Shared Doctors', href: '/shared-doctors', icon: Contact, roles: ['super_admin'], grant: 'canManageSharedPhysicians' },
      {
        name: 'Pharmacies', href: '/pharmacy', icon: Pill,
        roles: [...CONFIG_VIEWERS, 'pharmacy_manager', 'data_entry'],
        permission: 'canManagePharmacy',
      },
      {
        name: 'Drug Programs', href: '/drug-programs', icon: ClipboardList,
        roles: [...CONFIG_VIEWERS, 'program_manager', 'pharmacy_manager', 'data_entry'],
        permission: 'canManagePrograms',
      },
      {
        name: 'Program Categories', href: '/program-categories', icon: FolderTree,
        roles: [...CONFIG_VIEWERS, 'program_manager', 'data_entry'],
        permission: 'canManagePrograms',
      },
      {
        name: 'Releases', href: '/releases', icon: Rocket,
        roles: [...CONFIG_VIEWERS, 'program_manager', 'pharmacy_manager'],
        permission: 'canManageReleases',
      },
    ],
  },

  {
    // Support used to be its own four-item section. Its screens are operational
    // work, and a separate header for four items was most of what made the
    // sidebar feel long. Merged here; the audiences travel with the items.
    id: 'operations',
    name: 'Operations',
    icon: Wrench,
    items: [
      { name: 'Queues', href: '/ops/queues', icon: ListChecks, roles: OPERATIONS, shareable: true },
      { name: 'Order Flows', href: '/ops/order-flows', icon: Timer, roles: OPERATIONS, shareable: true },
      { name: 'Routing', href: '/ops/routing', icon: Map, roles: OPERATIONS, shareable: true },
      { name: 'Pharmacy Count', href: '/ops/pharmacy-count', icon: Building2, roles: OPERATIONS, shareable: true },
      { name: 'Rewards', href: '/ops/rewards', icon: Gift, roles: ['super_admin'] },
      { name: 'Zendesk', href: '/support/zendesk', icon: Headphones, roles: SUPPORT, clinics: ZENDESK_CLINICS },
      // Our own inbox (core physician_messaging), measured like Zendesk. Every
      // clinic has the channel, so no clinic gate; an empty one says so.
      { name: 'Provider Messaging', href: '/ops/provider-messaging', icon: Inbox, roles: SUPPORT },
      { name: 'Appointments', href: '/support/appointments', icon: Calendar, roles: SUPPORT, clinics: ['tmates'] },
      { name: 'Health Data', href: '/support/health-data', icon: HeartPulse, roles: SUPPORT },
      { name: 'Disqualifies', href: '/support/disqualifies', icon: Ban, roles: SUPPORT },
    ],
  },

  {
    // Every payments screen in one place. These were scattered through
    // Operations while Refunds sat alone under Accounting, so answering "what
    // happened to this charge" meant looking in two unrelated sections.
    id: 'billing',
    name: 'Billing',
    icon: CreditCard,
    items: [
      // Refunds, declines and per-patient net revenue on one screen. Tenant
      // clinics only: refunds and declines are read from Manifold.
      { name: 'CFO Report', href: '/ops/cfo', icon: Landmark, roles: ANALYTICS, shareable: true, clinics: MANIFOLD_TENANT_CLINICS },
      { name: 'Product Catalog', href: '/ops/catalog', icon: Package, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_CATALOG_CLINICS },
      { name: 'Coupons', href: '/ops/coupons', icon: Tag, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_CATALOG_CLINICS },
      // The transactional emails Manifold sends for the tenant. Billing rather
      // than Operations because that is what they are about — receipts,
      // renewals, dunning — and because they are edited alongside the catalog.
      { name: 'Email Templates', href: '/ops/email-templates', icon: Mail, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_TENANT_CLINICS },
      // Manifold-backed, not Chargebee-backed: the dashboard route resolves the
      // tenant from the selected clinic, so every tenant can see its own numbers.
      { name: 'Payments', href: '/ops/payments', icon: Banknote, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_TENANT_CLINICS },
      { name: 'Declines', href: '/ops/declines', icon: XCircle, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_TENANT_CLINICS },
      { name: 'Refunds', href: '/accounting/refunds', icon: Undo2, roles: ANALYTICS },
      // Manifold subscriptions, for every clinic billed there. Was "Subscriptions
      // (Hyperswitch)" and limited to MDExam/MDRX; the path is kept so links and
      // saved page permissions still resolve.
      { name: 'Subscriptions', href: '/ops/hs-subscriptions', icon: Repeat, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_TENANT_CLINICS },
      // Manifold's renewal checkout for customers who paid with Klarna/Afterpay/Affirm.
      { name: 'BNPL Renewals', href: '/ops/bnpl-renewals', icon: Wallet, roles: OPERATIONS, shareable: true, clinics: MANIFOLD_TENANT_CLINICS },
      // MDExam's Chargebee history. Only the two clinics it belongs to: legacy,
      // and WL — the pre-2026-07-01 patient base Chargebee billed.
      { name: 'Subscriptions (Chargebee)', href: '/ops/subscriptions', icon: RefreshCw, roles: OPERATIONS, shareable: true, clinics: ['mdexam_legacy', 'mdexam-wl'] },
      { name: 'Transactions', href: '/ops/hs-transactions', icon: Search, roles: OPERATIONS, shareable: true, clinics: HYPERSWITCH_CLINICS },
      // Not clinic-gated: it reads core alone. The order-transactions view it
      // replaced needed a Manifold tenant and a cross-cluster read; this does
      // not, so every clinic gets it.
      { name: 'New Customers', href: '/ops/new-customers', icon: UserPlus, roles: OPERATIONS, shareable: true },
      // Core-backed like the two above, so no clinic gate. Wide operational
      // export: one row per shipment rather than per charge.
      { name: 'Order Export', href: '/ops/order-export', icon: Table2, roles: OPERATIONS, shareable: true },
      // Core-only: reads orders and program_definition, so it is not clinic-gated.
      { name: 'Bundle Report', href: '/ops/bundle-report', icon: CalendarClock, roles: ANALYTICS },
      { name: 'Transaction Health', href: '/ops/transaction-health', icon: Activity, roles: OPERATIONS, shareable: true, clinics: CHARGEBEE_CLINICS },
      { name: 'API Logs', href: '/ops/api-logs', icon: Radio, roles: OPERATIONS, shareable: true, clinics: CHARGEBEE_CLINICS },
    ],
  },

  {
    // Reports and Accounting merged. They had been split down a line nobody
    // could hold in their head: P&L was a report, Multi P&L was accounting.
    // The sub-headers below carry the structure the flat 18-item list lacked.
    id: 'analytics',
    name: 'Analytics',
    icon: BarChart3,
    sections: [
      {
        name: 'Growth',
        items: [
          { name: 'Marketing', href: '/reports/marketing', icon: Megaphone, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'Traffic', href: '/reports/traffic', icon: Globe, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          // Today's pace by the hour vs the last 7 days, and the funnel, channels
          // and spend behind it. Science only for now: its funnel and hourly spend
          // are the only complete ones (see backend services/velocity).
          { name: 'Velocity', href: '/reports/velocity', icon: Activity, roles: ANALYTICS, requiresCurrentClinic: 'scienceco' },
          { name: 'Attribution', href: '/reports/attribution', icon: Route, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'Channels', href: '/reports/channels', icon: Share2, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'Product × Channel', href: '/reports/product-by-channel', icon: Layers, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'Growth Lanes', href: '/reports/lanes', icon: Network, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'Lift', href: '/reports/lift', icon: TrendingUp, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'Ad ↔ Landing Page', href: '/reports/ad-lp-alignment', icon: Link2, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
          { name: 'LP Click Rate', href: '/reports/lp-click-rate', icon: MousePointerClick, roles: ANALYTICS, clinics: LP_CLICK_RATE_CLINICS },
          { name: 'User Journey', href: '/reports/user-journey', icon: UserRound, roles: ANALYTICS, clinics: TRAFFIC_CLINICS },
        ],
      },
      {
        name: 'Revenue',
        items: [
          // The brand's trading table — the day-by-day numbers the P&L
          // summarises. First in Revenue because it is the one people open daily.
          { name: 'Daily Report', href: '/reports/daily', icon: CalendarDays, roles: ANALYTICS },
          { name: 'P&L', href: '/reports/pnl', icon: DollarSign, roles: ANALYTICS },
          // Science.co's growth model against what actually happened. Needs
          // Science selected, not merely accessible: the model is Science's
          // alone, and a general-purpose one waits on what this one teaches.
          { name: 'Model vs Actual', href: '/reports/model-vs-actual', icon: Target, roles: ANALYTICS, requiresCurrentClinic: 'scienceco' },
          // Per-rep sales on the sales_team register. Science.co is the only clinic
          // with a phone room stamping `rep` on patients; the API itself is generic.
          { name: 'Sales Team', href: '/reports/sales-team', icon: PhoneCall, roles: ANALYTICS, requiresCurrentClinic: 'scienceco' },
          { name: 'Multi-Clinic P&L', href: '/accounting/multi-pnl', icon: PieChart, roles: ANALYTICS },
          { name: 'Cost per Pharmacy', href: '/accounting/pharmacy-cost', icon: Receipt, roles: ANALYTICS },
          { name: 'Products', href: '/reports/product-marketing', icon: Boxes, roles: ANALYTICS },
          { name: 'Engagement', href: '/reports/engagement', icon: Clock, roles: ANALYTICS, clinics: CHARGEBEE_CLINICS },
          // Two sources behind one page: Chargebee for mdexam, Manifold for
          // every tenant. Gated on having one of them rather than on Chargebee.
          { name: 'Churn', href: '/reports/churn', icon: TrendingDown, roles: ANALYTICS, clinics: [...new Set([...CHARGEBEE_CLINICS, ...MANIFOLD_TENANT_CLINICS])] },
          { name: 'Cross-Sell', href: '/reports/cross-sell', icon: Shuffle, roles: ANALYTICS },
          // Manifold tenants whose active base bills there. MDExam is excluded:
          // most of its active patients are still on Chargebee.
          { name: 'Demographics', href: '/reports/demographics', icon: PersonStanding, roles: ANALYTICS, clinics: MANIFOLD_TENANT_CLINICS.filter(id => !CHARGEBEE_CLINICS.includes(id)) },
          // Moved out of Admin, where it was the only report among infra tools.
          { name: 'Weekly Report', href: '/admin/weekly-report', icon: FileText, roles: ['super_admin'] },
        ],
      },
      {
        name: 'Clinic',
        items: [
          // Super admin, or anyone with the canViewPrescribers grant. Not
          // clinic-gated: granted users see every Express clinic, super admins
          // see every clinic. The API enforces that split.
          { name: 'Prescribers', href: '/reports/prescribers', icon: FileSignature, roles: ['super_admin'], grant: 'canViewPrescribers' },
          { name: 'Dealer Tree', href: '/reports/dealer-tree', icon: GitBranch, roles: ANALYTICS, clinics: ['rose'] },
          // These two read a schema only the legacy MDExam database has, so
          // they need that clinic actually selected, not merely accessible.
          { name: 'Pharmacy Benefit', href: '/reports/pharmacy-benefit', icon: ShieldCheck, roles: ['super_admin'], requiresCurrentClinic: 'mdexam_legacy' },
          { name: 'Progress Boost', href: '/reports/progress-boost', icon: Gauge, roles: ['super_admin'], requiresCurrentClinic: 'mdexam_legacy' },
        ],
      },
    ],
  },

  {
    id: 'admin',
    name: 'Admin',
    icon: Shield,
    pinToBottom: true,
    items: [
      { name: 'Users', href: '/users', icon: Users, roles: ADMINS },
      { name: 'API Keys', href: '/admin/api-keys', icon: KeyRound, roles: ['super_admin'] },
      // Clinic admins too: the point of the page is that a clinic can connect
      // an assistant to their own data without asking anyone how.
      { name: 'MCP Access', href: '/admin/mcp', icon: Plug, roles: ADMINS },
      { name: 'Codex', href: '/admin/codex', icon: Bot, roles: ADMINS },
      { name: 'Express Setup', href: '/admin/express-setup', icon: Zap, roles: [...ADMINS, 'viewer', 'report_viewer'] },
      { name: 'Express Portals', href: '/admin/express-portals', icon: Server, roles: ['super_admin'] },
      // TEMPORARY: remove after the Firebase -> core portal auth cutover completes.
      //
      // Its own item beside Express Portals rather than a tab inside it: it
      // answers a different question than deploy health, and during a cutover
      // it is checked daily — a click down inside another page is where daily
      // checks go to die.
      //
      // Called "Portal Auth", NOT "Auth Migration" or anything else beginning
      // "Migration". `Migration Audit` two rows down is an unrelated migration
      // (MDExam legacy -> core), and two temporary cutover tools one row apart
      // both called "Migration something" is a mistake that gets made at speed
      // during an incident. "Portal Auth" is also the vocabulary the rest of
      // the system uses: the clinic feature flag is `portal_auth`, the
      // collections are `portal_*`, and the spec series is "Portal Auth Phase N".
      { name: 'Portal Auth', href: '/admin/portal-auth', icon: Fingerprint, roles: ['super_admin'] },
      // Its own item beside Portal Auth, and NOT a tab inside it: Portal Auth is
      // temporary cutover tooling that is deleted with Firebase, and this log
      // outlives it. Burying a permanent surface inside a page scheduled for
      // deletion is how it gets deleted with it.
      //
      // Called "Portal Impersonations", never just "Impersonations": this app
      // already has a super_admin-impersonates-a-user feature (`utils/jwt.js`,
      // `middleware/auth.js`), and two unrelated things one word apart in the
      // same nav is a mistake that gets made at speed during an incident.
      { name: 'Portal Impersonations', href: '/admin/portal-impersonations', icon: ScanFace, roles: ['super_admin'] },
      { name: 'Campaign Goals', href: '/admin/campaign-goals', icon: Crosshair, roles: ['super_admin'] },
      { name: 'Slack Notifications', href: '/admin/slack', icon: MessageSquare, roles: ['super_admin'] },
      { name: 'Uptime', href: '/admin/status', icon: Signal, roles: ADMINS },
      { name: 'File Storage', href: '/admin/file-storage', icon: FolderOpen, roles: ['super_admin'] },
      { name: 'Config Diff', href: '/admin/config-diff', icon: GitCompare, roles: ['super_admin'] },
      // TEMPORARY: remove after the mdexam legacy → core cutover completes
      { name: 'Migration Audit', href: '/admin/migration-audit', icon: Truck, roles: ['super_admin'] },
    ],
  },
]

/** Permission flags a clinic_admin may be restricted by. */
const PERMISSION_KEYS = ['canManagePharmacy', 'canManagePrograms', 'canManagePhysicians', 'canManageReleases']

/**
 * Whether a clinic_admin holds a permission.
 *
 * Only clinic_admins are gated. A clinic_admin with no flags configured at all
 * is a legacy account and passes everything — matching requirePermission in
 * backend/src/middleware/auth.js. Diverging from that would show links that
 * 403, or hide ones that work.
 */
function hasPermission(user, permission) {
  if (!permission) return true
  if (user?.role !== 'clinic_admin') return true
  const perms = user?.permissions || {}
  const configured = PERMISSION_KEYS.some(key => perms[key] !== undefined)
  return !configured || Boolean(perms[permission])
}

function hasClinicAccess(user, clinics, required) {
  if (!required) return true
  if (user?.role === 'super_admin') return true
  return (clinics || []).some(clinic => required.includes(clinic.id))
}

function isVisible(item, { user, clinics, currentClinicId }) {
  if (user?.role === 'report_viewer' && Array.isArray(user.permissions?.reportPages) &&
      !user.permissions.reportPages.includes(item.href)) return false
  const roleOk = item.roles === '*' || (item.roles || []).includes(user?.role)
  const grantOk = Boolean(item.grant && user?.permissions?.[item.grant]) ||
    Boolean(item.shareable && user?.role === 'report_viewer' &&
      Array.isArray(user.permissions?.reportPages) && user.permissions.reportPages.includes(item.href))
  if (!roleOk && !grantOk) return false
  if (!hasPermission(user, item.permission)) return false
  if (!hasClinicAccess(user, clinics, item.clinics)) return false
  if (item.requiresCurrentClinic && currentClinicId !== item.requiresCurrentClinic) return false
  return true
}

/** Read a group's items whether or not it uses sub-headers. */
function sectionsOf(group) {
  return group.sections || [{ name: null, items: group.items }]
}

/**
 * The navigation tree this user can actually reach.
 *
 * Sections with no surviving items are dropped, and so are groups left with no
 * sections — a header over an empty list is worse than no header.
 *
 * @param {Object} context - { user, clinics, currentClinicId }
 * @returns {{ dashboard: Object|null, groups: Array }}
 */
export function visibleNavigation(context = {}) {
  // tv_display is a kiosk role; it is redirected away from this layout entirely
  // and must never be offered a way back into the admin app.
  if (context.user?.role === 'tv_display') {
    return { dashboard: null, groups: [] }
  }

  const groups = NAV_GROUPS.map(group => {
    const sections = sectionsOf(group)
      .map(section => ({ ...section, items: section.items.filter(item => isVisible(item, context)) }))
      .filter(section => section.items.length > 0)

    return { ...group, sections }
  }).filter(group => group.sections.length > 0)

  return {
    dashboard: isVisible(DASHBOARD, context) ? DASHBOARD : null,
    groups,
  }
}

/**
 * Narrow a tree to items matching a search string.
 *
 * Matching a group name keeps the whole group, so typing "billing" behaves the
 * way typing a heading should. An empty query returns the tree untouched.
 */
export function filterNavigation(tree, query) {
  const needle = (query || '').trim().toLowerCase()
  if (!needle) return tree

  const groups = tree.groups.map(group => {
    if (group.name.toLowerCase().includes(needle)) return group
    const sections = group.sections
      .map(section => ({ ...section, items: section.items.filter(i => i.name.toLowerCase().includes(needle)) }))
      .filter(section => section.items.length > 0)
    return { ...group, sections }
  }).filter(group => group.sections.length > 0)

  const dashboard = tree.dashboard && tree.dashboard.name.toLowerCase().includes(needle)
    ? tree.dashboard
    : null

  return { dashboard, groups }
}

/** Every visible href, for tests and for the active-group calculation. */
export function flattenNavigation(tree) {
  return tree.groups.flatMap(group => group.sections.flatMap(section => section.items))
}

/** Page choices follow sidebar labels and grouping; clinic gates apply at viewing time. */
export function reportPageOptions(permissions = {}) {
  const eligible = item => Object.hasOwn(reportPages, item.href) &&
    (item.roles === '*' || item.roles.includes('report_viewer') || item.shareable ||
      (item.grant && permissions[item.grant]))
  return [
    { name: 'Overview', items: [DASHBOARD] },
    ...NAV_GROUPS.flatMap(group => sectionsOf(group).map(section => ({
      name: section.name ? `${group.name} · ${section.name}` : group.name,
      items: section.items.filter(eligible),
    }))),
  ].filter(group => group.items.length)
}

export function reportPageDestination(user, tree, pathname) {
  if (user?.role !== 'report_viewer' || !Array.isArray(user.permissions?.reportPages)) return undefined
  const aliases = { '/analytics': '/reports/pnl', '/reports/support': '/support/zendesk', '/reports/appointments': '/support/appointments' }
  const path = aliases[pathname] || pathname.replace(/\/$/, '') || '/'
  const items = [tree.dashboard, ...flattenNavigation(tree)].filter(Boolean)
  if (items.some(item => item.href === path)) return undefined
  return items[0]?.href || null
}
